import { createContext, useContext, useState, useEffect } from 'react'

const StoryContext = createContext(null)

export function StoryProvider({ children }) {
  const [userSettings, setUserSettings] = useState(() => {
    const saved = localStorage.getItem('storypath-settings')
    return saved ? JSON.parse(saved) : {
      ageGroup: null,      // 'little' (4-6), 'middle' (7-9), 'bigger' (10-12)
      culture: null,       // 'en-US', 'es-MX', etc.
      language: 'en',      // 'en' or 'es' - UI language
      learnerMode: false,  // true if learning a language
      nativeLanguage: null,
      targetLanguage: null
    }
  })

  const [currentStory, setCurrentStory] = useState(null)
  const [currentNode, setCurrentNode] = useState(null)
  const [storyHistory, setStoryHistory] = useState([])
  const [savedStories, setSavedStories] = useState(() => {
    const saved = localStorage.getItem('storypath-saved')
    return saved ? JSON.parse(saved) : []
  })

  // Persist settings
  useEffect(() => {
    localStorage.setItem('storypath-settings', JSON.stringify(userSettings))
  }, [userSettings])

  // Persist saved stories
  useEffect(() => {
    localStorage.setItem('storypath-saved', JSON.stringify(savedStories))
  }, [savedStories])

  // Load a story and start at the beginning
  const loadStory = (story) => {
    setCurrentStory(story)
    setCurrentNode(story.nodes.start)
    setStoryHistory(['start'])
  }

  // Make a choice and navigate to next node
  const makeChoice = (choice) => {
    if (!currentStory || !choice.next) return
    
    const nextNode = currentStory.nodes[choice.next]
    if (nextNode) {
      setCurrentNode(nextNode)
      setStoryHistory(prev => [...prev, choice.next])
    }
  }

  // Go back one step
  const goBack = () => {
    if (storyHistory.length <= 1) return
    
    const newHistory = [...storyHistory]
    newHistory.pop()
    const previousNodeId = newHistory[newHistory.length - 1]
    setCurrentNode(currentStory.nodes[previousNodeId])
    setStoryHistory(newHistory)
  }

  // Exit current story
  const exitStory = () => {
    setCurrentStory(null)
    setCurrentNode(null)
    setStoryHistory([])
  }

  // Save a generated story
  const saveGeneratedStory = (story) => {
    setSavedStories(prev => [...prev, { ...story, createdAt: Date.now() }])
  }

  const value = {
    userSettings,
    setUserSettings,
    currentStory,
    currentNode,
    storyHistory,
    savedStories,
    loadStory,
    makeChoice,
    goBack,
    exitStory,
    saveGeneratedStory
  }

  return (
    <StoryContext.Provider value={value}>
      {children}
    </StoryContext.Provider>
  )
}

export function useStory() {
  const context = useContext(StoryContext)
  if (!context) {
    throw new Error('useStory must be used within a StoryProvider')
  }
  return context
}
